import {
  Badge,
  Box,
  Card,
  CardBody,
  CircularProgress,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Heading,
  HStack,
  Icon,
  Text,
} from '@chakra-ui/react';
import moment from 'moment';
import { useCustomerContext } from 'pages/Customer/CustomerContext';
import { useMemo } from 'react';
import { MdInfoOutline } from 'react-icons/md';
import ClipboardBanner from 'shared/components/ClipboardBanner';
import { getStatusColor, getStatusLabel } from 'shared/helpers/orderStatus';
import { orderStatus } from 'shared/models/order';
import OrderActions from './components/OrderActions';
import OrderData from './components/OrderData';
import OrderForm from './components/OrderForm';
import OrderLogHistory from './components/OrderLogHistory';
import { useOrderModalContext } from './ModalContext';

interface OrderModalProps {
  isOpen: boolean;
  onClose(): void;
}

function calculateLatePrice(
  originalPrice: number,
  dueDate: moment.Moment,
): number {
  const now = moment();
  if (!dueDate || dueDate.isAfter(now)) return originalPrice;

  const daysLate = now.diff(dueDate, 'days');
  const interestRate = 0.025;
  const fineRate = 0.02;

  const interest = originalPrice * interestRate * (daysLate / 30);
  const fine = originalPrice * fineRate;
  return originalPrice + interest + fine;
}

const OrderModal: React.FC<OrderModalProps> = ({ isOpen, onClose }) => {
  const { loading, order, editing } = useOrderModalContext();
  const { customer } = useCustomerContext();

  const { label, color } = useMemo(
    () => ({
      color: getStatusColor(order?.statusId as orderStatus),
      label: getStatusLabel(order?.statusId as orderStatus),
    }),
    [order],
  );

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="xl">
      <DrawerOverlay />

      <DrawerContent>
        <DrawerCloseButton />
        {order && (
          <DrawerHeader>
            <Text fontSize="1rem" mb="0.5rem" textAlign="center">
              {customer.name}
            </Text>

            {order.bankId === 2 && order?.due_date && order?.hasInvoiceLink && (
              <Card
                bg={
                  order.due_date.isBefore(moment(), 'day')
                    ? 'red.100'
                    : 'blue.100'
                }
                marginBottom="18px"
              >
                <CardBody>
                  <HStack gap="1rem" align="center">
                    <Icon
                      as={MdInfoOutline}
                      color={
                        order.due_date.isBefore(moment(), 'day')
                          ? 'red.500'
                          : 'blue.500'
                      }
                    />
                    <Flex direction="column">
                      {order.due_date.isBefore(moment(), 'day') ? (
                        <>
                          <Text fontSize="0.9rem" color="red.500" mb={2}>
                            Boleto vencido! Juros e multa aplicados.
                          </Text>
                          <HStack spacing={2} mb={1}>
                            <Text fontSize="0.9rem" color="gray.600">
                              Original:
                            </Text>
                            <Text as="s" color="gray.400" fontWeight="500">
                              R$ {order.price?.toFixed(2)}
                            </Text>
                            <Text fontSize="0.9rem" color="red.500">
                              Novo valor previsto:
                            </Text>
                            <Text color="red.500" fontWeight="500">
                              R${' '}
                              {calculateLatePrice(
                                order.price,
                                order.due_date,
                              ).toFixed(2)}
                            </Text>
                          </HStack>
                          <Text fontSize="0.9rem" color="gray.600">
                            • Juros de 2,5% ao mês (calculados por dia de
                            atraso)
                            <br />• Multa fixa de 2% sobre o valor original
                          </Text>
                        </>
                      ) : (
                        <Text
                          fontSize="0.9rem"
                          fontWeight="400"
                          color="gray.600"
                        >
                          Atenção: após o vencimento, o boleto terá acréscimo
                          de:
                          <br />
                          • Juros de 2,5% ao mês (calculados por dia de atraso)
                          <br />• Multa fixa de 2% sobre o valor original
                        </Text>
                      )}
                    </Flex>
                  </HStack>
                </CardBody>
              </Card>
            )}

            <HStack>
              <Heading size="md">Cobrança #{order.id}</Heading>
              <Badge bg={color} color="white">
                {label}
              </Badge>
            </HStack>

            {order.bankId === 1 && order.statusId === 1 && (
              <Box marginTop="0.5rem" marginBottom="0.5rem">
                {' '}
                <ClipboardBanner
                  content="Link para pagamento:"
                  colorScheme="yellow"
                  copyText={order.paymentLink}
                  successCopyMessage="Link copiado"
                  size="sm"
                  variant="default"
                />
              </Box>
            )}

            <Text fontSize="0.8rem" color="gray.500">
              {order.description}
            </Text>

            <OrderActions />
          </DrawerHeader>
        )}

        <DrawerBody pos="relative">
          {editing ? <OrderForm /> : <OrderData />}
          <OrderLogHistory />

          {loading && (
            <Flex
              pos="absolute"
              top="0"
              left="0"
              w="100%"
              h="100%"
              bg="gray.100"
              zIndex="1"
              align="center"
              justify="center"
            >
              <CircularProgress isIndeterminate color="yooga.400" />
            </Flex>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export default OrderModal;
